/**
 * Sentry Configuration for SmartFacture AI Frontend
 * Error monitoring and performance tracking
 */

import * as Sentry from "@sentry/react";

// Liste des clés sensibles à masquer
const SENSITIVE_KEYS = [
  'password', 'token', 'access_token', 'refresh_token', 'api_key',
  'secret', 'authorization', 'iban', 'credit_card', 'card_number',
  'cvv', 'siren', 'siret', 'email', 'phone', 'address'
];

/**
 * Masque les données sensibles avant envoi à Sentry
 */
const scrubSensitiveData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const scrubbed = Array.isArray(data) ? [] : {};
  
  for (const [key, value] of Object.entries(data)) {
    const keyLower = key.toLowerCase();
    
    if (SENSITIVE_KEYS.some(sensitive => keyLower.includes(sensitive))) {
      if (typeof value === 'string' && value.length > 4) {
        scrubbed[key] = value.substring(0, 2) + '***' + value.substring(value.length - 2);
      } else {
        scrubbed[key] = '[FILTERED]';
      }
    } else if (typeof value === 'object') {
      scrubbed[key] = scrubSensitiveData(value);
    } else {
      scrubbed[key] = value;
    }
  }
  
  return scrubbed;
};

/**
 * Initialise Sentry avec la configuration complète
 */
export const initSentry = () => {
  const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
  
  if (!SENTRY_DSN) {
    console.log('Sentry: DSN non configuré, monitoring désactivé');
    return false;
  }
  
  Sentry.init({
    dsn: SENTRY_DSN,
    
    // Performance monitoring
    tracesSampleRate: 0.3, // 30% des transactions
    
    // Environment et release
    environment: process.env.NODE_ENV || 'production',
    release: `smartfacture-frontend@${process.env.REACT_APP_VERSION || '1.0.0'}`,
    
    // Intégrations
    integrations: [
      // Browser tracing automatique
      Sentry.browserTracingIntegration({
        // URLs à tracer
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/.*\.emergentagent\.com/,
          /^https:\/\/smartfacture-ai\.fr/,
        ],
      }),
      // Replay pour debug (optionnel, désactivé par défaut)
      // Sentry.replayIntegration(),
    ],
    
    // Options de sécurité
    beforeSend(event, hint) {
      // Masquer données sensibles
      if (event.request?.data) {
        event.request.data = scrubSensitiveData(event.request.data);
      }
      if (event.extra) {
        event.extra = scrubSensitiveData(event.extra);
      }
      
      // Filtrer les erreurs de script externe
      const error = hint?.originalException;
      if (error && error.message) {
        // Ignorer les erreurs de réseau
        if (error.message.includes('Network Error')) {
          return null;
        }
        // Ignorer les erreurs de scripts tiers
        if (error.message.includes('Script error')) {
          return null;
        }
      }
      
      return event;
    },
    
    // Ignorer certaines erreurs courantes
    ignoreErrors: [
      // Erreurs réseau
      'Network Error',
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // Erreurs de navigation
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Erreurs de scripts tiers
      /^Script error\.?$/,
      /^Javascript error: Script error\.? on line 0$/,
    ],
    
    // Ne pas envoyer les PII par défaut
    sendDefaultPii: false,
  });
  
  // Tags globaux
  Sentry.setTag('service', 'smartfacture-frontend');
  Sentry.setTag('platform', 'web');
  
  console.log('Sentry: Monitoring initialisé avec succès');
  return true;
};

/**
 * Capture une erreur manuellement
 */
export const captureError = (error, context = {}) => {
  Sentry.captureException(error, {
    extra: scrubSensitiveData(context),
  });
};

/**
 * Capture un message/événement
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  Sentry.captureMessage(message, {
    level,
    extra: scrubSensitiveData(context),
  });
};

/**
 * Définit l'utilisateur courant (sans données sensibles)
 */
export const setUser = (user) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      // Ne pas envoyer l'email complet
      username: user.email ? user.email.split('@')[0] + '@***' : undefined,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Ajoute des tags personnalisés
 */
export const setTags = (tags) => {
  for (const [key, value] of Object.entries(tags)) {
    Sentry.setTag(key, value);
  }
};

/**
 * Error Boundary wrapper pour React
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * HOC pour wrapper les composants avec error boundary
 */
export const withSentryErrorBoundary = Sentry.withErrorBoundary;

export default Sentry;
